import * as THREE from 'three';
import { World } from './world';
import { BLOCKS } from './blocks';
import { Inventory } from './inventory';

const REACH = 6;
const RAY_STEP = 0.02;

interface TargetBlock {
  position: THREE.Vector3;
  normal: THREE.Vector3;
}

export class Interaction {
  private world: World;
  private camera: THREE.PerspectiveCamera;
  private scene: THREE.Scene;
  private highlight: THREE.LineSegments;
  private highlightVisible = false;
  private inventory: Inventory;

  // Callback fired when player right-clicks an interactable block (e.g. bed, music, firework, TNT)
  onBedInteract: ((wx: number, wy: number, wz: number) => void) | null = null;
  onInteract: ((wx: number, wy: number, wz: number, blockId: number) => void) | null = null;
  // Player yaw for directional block placement
  playerYaw: number = 0;

  constructor(world: World, camera: THREE.PerspectiveCamera, scene: THREE.Scene, inventory: Inventory) {
    this.world = world;
    this.camera = camera;
    this.scene = scene;
    this.inventory = inventory;

    const boxGeo = new THREE.BoxGeometry(1.01, 1.01, 1.01);
    const edgesGeo = new THREE.EdgesGeometry(boxGeo);
    const lineMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.6,
      depthTest: true,
    });
    this.highlight = new THREE.LineSegments(edgesGeo, lineMat);
    this.highlight.visible = false;
    this.scene.add(this.highlight);
    boxGeo.dispose();
  }

  getTargetBlock(): TargetBlock | null {
    const direction = new THREE.Vector3();
    this.camera.getWorldDirection(direction);
    const origin = this.camera.position.clone();

    const steps = Math.ceil(REACH / RAY_STEP);
    let lastAirX = Math.floor(origin.x);
    let lastAirY = Math.floor(origin.y);
    let lastAirZ = Math.floor(origin.z);
    let hadAir = false;

    const startBlock = this.world.getBlock(lastAirX, lastAirY, lastAirZ);
    if (startBlock === 0 || !BLOCKS[startBlock]?.solid) {
      hadAir = true;
    }

    for (let i = 1; i <= steps; i++) {
      const px = origin.x + direction.x * i * RAY_STEP;
      const py = origin.y + direction.y * i * RAY_STEP;
      const pz = origin.z + direction.z * i * RAY_STEP;
      const bx = Math.floor(px);
      const by = Math.floor(py);
      const bz = Math.floor(pz);

      const blockId = this.world.getBlock(bx, by, bz);

      if (blockId === 0 || !BLOCKS[blockId]?.solid) {
        lastAirX = bx;
        lastAirY = by;
        lastAirZ = bz;
        hadAir = true;
        continue;
      }

      const normal = new THREE.Vector3(0, 1, 0);

      if (hadAir) {
        const dx = lastAirX - bx;
        const dy = lastAirY - by;
        const dz = lastAirZ - bz;

        const ax = Math.abs(dx);
        const ay = Math.abs(dy);
        const az = Math.abs(dz);

        if (ax >= ay && ax >= az) {
          normal.set(dx > 0 ? 1 : -1, 0, 0);
        } else if (ay >= ax && ay >= az) {
          normal.set(0, dy > 0 ? 1 : -1, 0);
        } else {
          normal.set(0, 0, dz > 0 ? 1 : -1);
        }
      }

      return {
        position: new THREE.Vector3(bx, by, bz),
        normal,
      };
    }

    return null;
  }

  update() {
    const target = this.getTargetBlock();
    if (target) {
      this.highlight.position.set(
        target.position.x + 0.5,
        target.position.y + 0.5,
        target.position.z + 0.5
      );
      if (!this.highlightVisible) {
        this.highlight.visible = true;
        this.highlightVisible = true;
      }
    } else {
      if (this.highlightVisible) {
        this.highlight.visible = false;
        this.highlightVisible = false;
      }
    }
  }

  onLeftClick(): boolean {
    const target = this.getTargetBlock();
    if (!target) return false;

    const { position } = target;
    const blockId = this.world.getBlock(position.x, position.y, position.z);
    if (blockId === 18) return false; // bedrock

    const block = BLOCKS[blockId];
    if (!block) return false;

    // Determine what item to drop
    const dropId = block.dropsItem ?? blockId;

    // Remove block from world
    this.world.setBlock(position.x, position.y, position.z, 0);

    // Add to inventory
    if (dropId !== 0 && this.inventory) {
      this.inventory.add(dropId);
    }

    return true;
  }

  onRightClick(blockId: number): boolean {
    const target = this.getTargetBlock();
    if (!target) return false;

    // Check if the targeted block is interactable (e.g. bed, music block, firework)
    const targetBlockId = this.world.getBlock(target.position.x, target.position.y, target.position.z);
    const targetBlock = BLOCKS[targetBlockId];
    if (targetBlock?.interactable) {
      // Bed blocks use the legacy onBedInteract callback
      if (targetBlockId === 48 || targetBlockId === 49) {
        this.onBedInteract?.(target.position.x, target.position.y, target.position.z);
      } else {
        this.onInteract?.(target.position.x, target.position.y, target.position.z, targetBlockId);
      }
      return true;
    }

    // Check inventory has the block
    const block = BLOCKS[blockId];
    if (!block || block.isItem) return false; // can't place items
    if (this.inventory && !this.inventory.has(blockId)) return false;

    const placeX = target.position.x + target.normal.x;
    const placeY = target.position.y + target.normal.y;
    const placeZ = target.position.z + target.normal.z;

    // Don't place if occupied
    const existing = this.world.getBlock(placeX, placeY, placeZ);
    if (existing !== 0 && BLOCKS[existing]?.solid) return false;

    // Don't place inside player
    const camPos = this.camera.position;
    const playerMinY = camPos.y - 1.6;
    const playerMaxY = camPos.y + 0.2;
    const px = Math.floor(camPos.x);
    const pz = Math.floor(camPos.z);
    if (
      placeX === px &&
      placeZ === pz &&
      placeY >= Math.floor(playerMinY) &&
      placeY <= Math.floor(playerMaxY)
    ) {
      return false;
    }

    // Consume from inventory and place
    if (this.inventory) this.inventory.remove(blockId);

    // Directional Pijlblok: remap block 55 based on player yaw
    let placeId = blockId;
    if (blockId === 55) {
      // Normalize yaw to 0-2PI
      const yaw = ((this.playerYaw % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      // yaw 0 = looking toward -Z (north)
      // Determine cardinal: N(55), E(56), S(57), W(58)
      if (yaw >= Math.PI * 0.25 && yaw < Math.PI * 0.75) {
        placeId = 58; // West (looking toward -X)
      } else if (yaw >= Math.PI * 0.75 && yaw < Math.PI * 1.25) {
        placeId = 57; // South (looking toward +Z)
      } else if (yaw >= Math.PI * 1.25 && yaw < Math.PI * 1.75) {
        placeId = 56; // East (looking toward +X)
      } else {
        placeId = 55; // North (looking toward -Z)
      }
    }

    this.world.setBlock(placeX, placeY, placeZ, placeId);
    return true;
  }

  dispose() {
    this.scene.remove(this.highlight);
    this.highlight.geometry.dispose();
    (this.highlight.material as THREE.LineBasicMaterial).dispose();
  }
}
